/**
 * WMS raster overlay registry (#212) — the two official government WMS rasters
 * mounted as toggleable AMBIENT overlays via the generic `WmsOverlayLayer`. Both
 * are client-side (open CORS, live-verified), default OFF, and honest-degrade.
 *
 *   1. ECCC GeoMet — hurricane forecast track line (pairs with the #217 NHC
 *      vector track; this is the official ECCC render). Atlantic-season only.
 *   2. NRCan EGS — Active-Floods observed flood-extent raster. Off-season empty.
 *
 * NOTE on the "L1–L4 flood-extent raster" framing (#212 inventory): the live
 * NRCan EGS service does NOT expose L1–L4 likelihood bands. It serves Active /
 * Current / Archive flood products, each with Flooded-Area / Floods /
 * Product-Footprint sublayers. We mount the **Active** product's flooded-area
 * polygons (layer `0`) — the authoritative near-real-time observed extent. (Same
 * "inventory assumed a shape the live service doesn't have" correction as #205.)
 */
import * as Cesium from 'cesium';
import type { LayerKey } from '@tsr/shared-types';
import type { LayerPrefs } from '../prefs';
import type { WmsOverlayConfig } from './WmsOverlayLayer';

/** Atlantic-basin tiling extent for the hurricane track (storms approach SE). */
const ATLANTIC_RECT = Cesium.Rectangle.fromDegrees(-100.0, 10.0, -40.0, 70.0);
/** Canada-wide tiling extent for the flood-extent raster (CRS:84 footprint). */
const CANADA_RECT = Cesium.Rectangle.fromDegrees(-141.5, 41.0, -52.0, 84.0);

/** A registry entry binds a WMS overlay config to its persisted layer key/pref. */
export interface WmsOverlayRegistryEntry extends WmsOverlayConfig {
  layerKey: LayerKey;
  prefKey: keyof LayerPrefs;
}

export const WMS_OVERLAYS: readonly WmsOverlayRegistryEntry[] = [
  {
    key: 'eccc-hurricane-track',
    layerKey: 'eccc-hurricane-track',
    prefKey: 'ecccHurricaneTrack',
    label: 'Hurricane Forecast Track (ECCC)',
    url: 'https://geo.weather.gc.ca/geomet',
    // GeoMet rejects multi-value LAYERS, so a single layer: the forecast track
    // line (the "track" the task asks for; the error cone is a separate layer).
    layers: 'HURRICANE_LINE',
    alpha: 0.55,
    attribution: '© Environment and Climate Change Canada (GeoMet)',
    rectangle: ATLANTIC_RECT,
  },
  {
    key: 'nrcan-flood-extent',
    layerKey: 'nrcan-flood-extent',
    prefKey: 'nrcanFloodExtent',
    label: 'Flood Extent (NRCan EGS)',
    url: 'https://maps-cartes.services.geo.ca/egs_sgu/services/Flood_Inondation/EGS_Flood_Product_Active_en/MapServer/WMSServer',
    // Layer 0 = "Active Flooded Area in Canada" — observed flood-extent polygons.
    layers: '0',
    alpha: 0.5,
    attribution:
      'Contains data © Natural Resources Canada (EGS), Open Government Licence – Canada',
    rectangle: CANADA_RECT,
  },
];

/** Project the layer prefs into the per-key visibility map MapView consumes. */
export function wmsVisibilityFromPrefs(prefs: LayerPrefs): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const o of WMS_OVERLAYS) out[o.key] = Boolean(prefs[o.prefKey]);
  return out;
}

/** Stable empty map for surfaces that mount no WMS overlays. */
export const EMPTY_WMS_VISIBILITY: Record<string, boolean> = {};

/* ───────────────────────── WALL WMS overlays (PART XI) ─────────────────────── *
 *  #272, design-council PART XI Ruling 1.4 — ECCC GeoMet live radar is ADMITTED
 *  to the wall hero with constraints. Radar is a WMS overlay, NOT a layer row:
 *  it has no LayerKey/pref and never mounts on the operator COP — these
 *  registry entries install ONLY on the WALL MapView, and the wall-default set
 *  below rides the same pinned lock test as WALL_VIEW_LAYERS
 *  (wallViewLock.test.ts).
 * ───────────────────────────────────────────────────────────────────────────── */

/** Wall-hero ambient ceiling for the radar raster (Ruling 1.4a) — BELOW the
 *  0.55 FWI/WMS ceiling: radar is full-country and self-refreshing, so it gets
 *  less ink than a seasonal regional raster. */
export const WALL_RADAR_MAX_ALPHA = 0.4;

/** GeoMet radar refresh cadence (Ruling 1.4d) — the service publishes a new
 *  pass every ~6 min (GetCapabilities TIME step PT6M, live-verified
 *  2026-06-12); we re-fetch on the same cadence with an honest timestamp line. */
export const WALL_RADAR_REFRESH_MS = 6 * 60_000;

/**
 * The radar STYLES= pick (Ruling 1.4b — "no red impersonation"): legend
 * graphics for all 16 RADAR_1KM_RRAI styles were pixel-decoded 2026-06-12.
 * The DEFAULT continuous ramp (`Radar-Rain`) ramps through rgb(254,19,32) —
 * ΔRGB ≈ 43 from URGENT-RED #ff3b30 — and is therefore FORBIDDEN on the wall.
 * `Radar-Rain_Dis-8colors` is the compliant discrete ramp: greens (light) →
 * violets (mid) → magenta #fe66cb (heaviest); it contains ZERO red-family
 * bins, and its nearest colour to URGENT-RED is that magenta at ΔRGB ≈ 161 —
 * unmistakable at wall distance even before the α≤0.40 clamp. (Greens at
 * ≤0.4α raster wash don't collide with the live-camera green family on the
 * wall: cameras are OFF the wall map per Ruling 1.2.)
 */
export const WALL_RADAR_STYLE = 'Radar-Rain_Dis-8colors';

/** Styles whose ramp reads URGENT-RED at distance (pixel-decoded legends) —
 *  pinned so a future style swap can't silently reintroduce impersonation. */
export const RADAR_FORBIDDEN_STYLES: readonly string[] = [
  '', // server default = Radar-Rain
  'Radar-Rain',
  'Radar-Rain_Dis',
];

/** A wall WMS overlay: a plain WmsOverlayConfig (no LayerKey/pref — never a
 *  COP layer row) whose wall-default visibility is locked below. */
export interface WallWmsOverlayEntry extends WmsOverlayConfig {
  /** Wall default-on state — pinned by wallViewLock.test.ts. Radar is
   *  DEFAULT-ON as of the founder screenshot acceptance (2026-06-12, gate
   *  cleared — Ruling 1.4e / the #197 pattern); flipping it = editing the lock
   *  test = founder sign-off. */
  wallDefaultOn: boolean;
}

export const WALL_WMS_OVERLAYS: readonly WallWmsOverlayEntry[] = [
  {
    key: 'geomet-radar',
    label: 'Live Radar (ECCC GeoMet)',
    url: 'https://geo.weather.gc.ca/geomet',
    // Single layer per request (the #174 GeoMet gotcha): the 1km rain-rate
    // composite. TIME omitted = the latest pass.
    layers: 'RADAR_1KM_RRAI',
    styles: WALL_RADAR_STYLE,
    alpha: WALL_RADAR_MAX_ALPHA,
    attribution: '© Environment and Climate Change Canada (GeoMet)',
    rectangle: CANADA_RECT,
    refreshMs: WALL_RADAR_REFRESH_MS,
    // Founder screenshot gate CLEARED 2026-06-12 (Ruling 1.4e, the #197
    // pattern) — radar is ambient default-ON on the wall hero at α≤0.40 with
    // the pinned no-red ramp. Editing this = founder sign-off (lock test).
    wallDefaultOn: true,
  },
];

/** The wall's WMS visibility map: the locked defaults, plus the active tour
 *  stop's overlays and the `?radar=1` founder-gate QA override on top. */
export function wallWmsVisibility(extraOnKeys: readonly string[] = []): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const o of WALL_WMS_OVERLAYS) {
    out[o.key] = o.wallDefaultOn || extraOnKeys.includes(o.key);
  }
  return out;
}
